import { MutableRefObject, useCallback, useMemo } from 'react';
import type { GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { useDocumentationStore } from 'store/documentation';
import {
  DocumentationViewItem,
  isDirectoryItemType,
} from 'store/documentation/model/types';
import { getRowClassName as getBaseRowClassName, hasOnlyNestedFilterMatches } from '../model';

interface GridRowClassNameParamsInput {
  isDraggingOver: boolean;
  dropTargetRef: MutableRefObject<{ id: string; name: string | null }>;
}

/** Row classes: pending, drag-over target, matching-files folder, recently uploaded. */
export function useGridRowClassName({
  isDraggingOver,
  dropTargetRef,
}: GridRowClassNameParamsInput): (params: GridRowClassNameParams<DocumentationViewItem>) => string {
  const pendingRowIds = useDocumentationStore.use.pendingRowIds();
  const uploadedDirectoryHighlightIds = useDocumentationStore.use.uploadedDirectoryHighlightIds();
  const uploadedDocumentationHighlightLocalIds =
    useDocumentationStore.use.uploadedDocumentationHighlightLocalIds();

  const uploadedDirectoryHighlights = useMemo(
    () => new Set(uploadedDirectoryHighlightIds),
    [uploadedDirectoryHighlightIds]
  );
  const uploadedDocumentationHighlights = useMemo(
    () => new Set(uploadedDocumentationHighlightLocalIds),
    [uploadedDocumentationHighlightLocalIds]
  );

  return useCallback(
    (params: GridRowClassNameParams<DocumentationViewItem>) => {
      const rowId = params.row._id;

      // Pending rows get special styling (opacity, disabled look)
      if (rowId && pendingRowIds.has(rowId)) {
        return 'row-pending';
      }

      const base = getBaseRowClassName(params);
      const isDirectory = isDirectoryItemType(params.row.type);

      const shouldHighlight =
        isDraggingOver &&
        isDirectory &&
        params.row._id &&
        dropTargetRef.current.id === params.row._id;

      const shouldShowTooltip = hasOnlyNestedFilterMatches(params.row);
      const isUploadedDirectoryHighlighted =
        isDirectory && typeof rowId === 'string' && uploadedDirectoryHighlights.has(rowId);
      const isUploadedDocumentationHighlighted =
        !isDirectory &&
        typeof params.row.localId === 'number' &&
        uploadedDocumentationHighlights.has(params.row.localId);

      let className = shouldHighlight ? base + ' drag-over' : base;
      if (shouldShowTooltip) className += ' directory-with-matching-files';
      if (isUploadedDirectoryHighlighted || isUploadedDocumentationHighlighted) {
        className += ' recently-uploaded';
      }
      return className;
    },
    [isDraggingOver, dropTargetRef, uploadedDirectoryHighlights, uploadedDocumentationHighlights, pendingRowIds]
  );
}
